import { motion } from 'framer-motion'
import { Circle } from 'lucide-react'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import Column from '@/components/core/Column.tsx'
import { useAutoPlay } from '@/contexts/AutoPlayContext'
import { useTestChat } from '@/contexts/TestChatContext'
import { cn } from '@/helpers/classNameBuilder'

const VideoRecordingBox: FC = () => {
  const { playingUrl } = useAutoPlay()
  const { startStream, messages, isStreamConnecting } = useTestChat()
  const videoRef = useRef<HTMLVideoElement>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)

  // Audio mixing refs
  const audioContextRef = useRef<AudioContext | null>(null)
  const destinationRef = useRef<MediaStreamAudioDestinationNode | null>(null)
  const mirrorAudioRef = useRef<HTMLAudioElement | null>(null)
  const mirrorSourceRef = useRef<MediaElementAudioSourceNode | null>(null)

  const [hasError, setHasError] = useState(false)

  const hasTestStarted = useMemo(() => !!messages.length, [messages])

  // Acquire camera and connect KVS immediately on mount
  useEffect(() => {
    let mounted = true

    const acquireCameraAndConnect = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true
        })

        if (!mounted) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }

        mediaStreamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
        }

        // Set up AudioContext to mix mic + bot audio for streaming
        const audioContext = new AudioContext()
        audioContextRef.current = audioContext
        await audioContext.resume()
        const destination = audioContext.createMediaStreamDestination()
        destinationRef.current = destination

        // Connect mic to the recording destination
        const micSource = audioContext.createMediaStreamSource(stream)
        micSource.connect(destination)

        // Build recording stream: camera video + mixed audio
        const videoTrack = stream.getVideoTracks()[0]
        const recordingStream = new MediaStream([
          videoTrack,
          ...destination.stream.getAudioTracks()
        ])

        // Connect KVS immediately — handleStart waits for this to complete
        await startStream(recordingStream)
      } catch (error) {
        console.error('Failed to start video recording:', error)
        if (mounted) setHasError(true)
      }
    }

    acquireCameraAndConnect()

    return () => {
      mounted = false
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop())
      if (mirrorAudioRef.current) {
        mirrorAudioRef.current.pause()
        mirrorAudioRef.current.src = ''
      }
      audioContextRef.current?.close()
    }
  }, [startStream])

  // Mirror bot audio into the recording stream
  useEffect(() => {
    const audioContext = audioContextRef.current
    const destination = destinationRef.current
    if (!audioContext || !destination) return

    if (playingUrl) {
      // Clean up previous mirror audio
      if (mirrorSourceRef.current) {
        mirrorSourceRef.current.disconnect()
        mirrorSourceRef.current = null
      }
      if (mirrorAudioRef.current) {
        mirrorAudioRef.current.pause()
        mirrorAudioRef.current.src = ''
      }

      const audio = new Audio()
      audio.crossOrigin = 'anonymous'
      audio.src = playingUrl
      mirrorAudioRef.current = audio

      const source = audioContext.createMediaElementSource(audio)
      source.connect(destination) // recording only, NOT audioContext.destination
      mirrorSourceRef.current = source

      audio.play().catch(() => {
        // Autoplay may be blocked; audio will still be captured when it starts
      })
    } else {
      if (mirrorSourceRef.current) {
        mirrorSourceRef.current.disconnect()
        mirrorSourceRef.current = null
      }
      if (mirrorAudioRef.current) {
        mirrorAudioRef.current.pause()
        mirrorAudioRef.current.src = ''
        mirrorAudioRef.current = null
      }
    }
  }, [playingUrl])

  if (hasError) return null

  return (
    <div
      className={cn(
        'fixed bottom-6 right-6 z-30 h-[180px] w-[280px]',
        'max-lg:top-2 max-lg:right-2 max-lg:h-25 max-lg:w-38 max-lg:rounded-xl',
        'overflow-hidden rounded-2xl',
        'bg-electric-indigo-900',
        'shadow'
      )}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="h-full w-full object-cover"
      />
      {hasTestStarted && (
        <Column className="absolute rounded-full top-2 right-3 z-20">
          <Column className="relative justify-center items-center">
            <Circle className="text-red-700" size={19} />
            <motion.div
              className="flex w-[11px] h-[11px] rounded-full bg-red-700 absolute"
              animate={{ scale: [1, 0.9, 1] }}
              transition={{
                duration: 1,
                ease: 'linear',
                repeat: Infinity
              }}
            />
          </Column>
        </Column>
      )}
      {isStreamConnecting && (
        <Column className="absolute justify-center items-center z-20 top-0 left-0 w-full h-full">
          <Spiner />
        </Column>
      )}
    </div>
  )
}

const Spiner = () => (
  <svg
    viewBox="0 0 38 38"
    xmlns="http://www.w3.org/2000/svg"
    stroke="currentColor"
    className="text-white h-7 w-7"
  >
    <g fill="none" fillRule="evenodd">
      <g transform="translate(1 1)" strokeWidth="2">
        <circle strokeOpacity=".5" cx="18" cy="18" r="18" />
        <path d="M36 18c0-9.94-8.06-18-18-18">
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 18 18"
            to="360 18 18"
            dur="1s"
            repeatCount="indefinite"
          />
        </path>
      </g>
    </g>
  </svg>
)

export default VideoRecordingBox
